/**
 * Book customer forms are version-4 UUIDs (`Form` uses HasVersion4Uuids).
 * Fill URLs are sometimes opened with trailing punctuation copied from email
 * or SMS quotes (MOVEPRO-BOOK-AW: `…/{uuid}'`). The form exists; the extra
 * character makes Laravel route-model binding 404.
 *
 * Truncated / non-UUID tokens (MOVEPRO-BOOK-AN: `YTAyYmRjOW`) cannot be
 * recovered here — FillForm must send those load failures to FormUnavailable.
 */
const FORM_UUID = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

export function customerFormIdFromParam(value: unknown): string {
    if (Array.isArray(value)) {
        return String(value[0] ?? '').trim();
    }

    return String(value ?? '').trim();
}

export function normalizeCustomerFormId(value: unknown): string {
    const raw = customerFormIdFromParam(value);
    const match = raw.match(FORM_UUID);

    return match ? match[0] : raw;
}

/**
 * vee-validate `setErrors` calls `Object.keys` on its argument. Book's
 * `processError` leaves `errors: null` for 404 / network / non-422 failures,
 * and a thrown TypeError has no `errors` key. Passing that through crashes
 * the catch handler (MOVEPRO-BOOK-AW) after the real failure was already
 * notified.
 */
export function applyBookFormFieldErrors(
    setErrors: (errors: Record<string, string | string[]>) => void,
    error: unknown,
): boolean {
    const errors = (error as { errors?: unknown } | null | undefined)?.errors;

    if (errors === null || errors === undefined || typeof errors !== 'object' || Array.isArray(errors)) {
        return false;
    }

    setErrors(errors as Record<string, string | string[]>);

    return true;
}

/**
 * Load failures that already have a dedicated interceptor destination
 * (session expired / forbidden / maintenance / API down) must not be
 * overwritten with FormUnavailable.
 */
export function shouldOpenFormUnavailableForLoadError(error: unknown): boolean {
    const code = (error as { code?: number | string | null } | null | undefined)?.code;
    const message = (error as { message?: string } | null | undefined)?.message;

    if (code === 401 || code === 403 || code === 503) {
        return false;
    }

    if (message === 'Network error') {
        return false;
    }

    return true;
}

/**
 * Expected missing-form traffic: GET /customers/forms/:id → 404.
 * Do not match nested paths such as form-submissions — those 404s are unexpected.
 */
export function isCustomerFormShowNotFound(error: unknown): boolean {
    const e = error as {
        response?: { status?: number };
        config?: { method?: string; url?: string };
    } | null;

    if (e?.response?.status !== 404) {
        return false;
    }

    const method = String(e?.config?.method ?? 'get').toLowerCase();
    if (method !== 'get') {
        return false;
    }

    const url = String(e?.config?.url ?? '');

    return /\/customers\/forms\/[^/?#]+\/?$/.test(url);
}
